import React from 'react';
import { UnscheduledEvent } from '../../types';
import { extractDate, extractTime, getErtsTimeDisplay } from './dateTimeUtils';

/**
 * Props for the TimeDeclaredSection component.
 */
interface TimeDeclaredSectionProps {
  formData: UnscheduledEvent;
  isReadOnly: boolean;
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  label?: string;
  /** Highlights the date/time as required when set and still empty. */
  dateRequired?: boolean;
  /** Marks a Triage-populated date as requiring user verification. */
  verifyDate?: boolean;
  /** Marks a Triage-populated time as requiring user verification. */
  verifyTime?: boolean;
}

/**
 * Time Declared + Time Entered + Created By row in the event modal.
 */
const TimeDeclaredSection: React.FC<TimeDeclaredSectionProps> = ({
  formData,
  isReadOnly,
  updateField,
  label,
  dateRequired,
  verifyDate,
  verifyTime,
}) => {
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const time = extractTime(formData.timeDeclaredZulu) || '';
    const newVal = time ? `${e.target.value} ${time}:00` : e.target.value;
    updateField('timeDeclaredZulu', newVal);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 4) val = val.substring(0, 4);
    const formatted = val.length >= 3 ? val.substring(0, 2) + ':' + val.substring(2) : val;
    const date = formData.timeDeclaredZulu ? formData.timeDeclaredZulu.split(' ')[0] : '';
    const newVal =
      date && formatted.length === 5 ? `${date} ${formatted}:00` : date ? `${date} ${val}` : val;
    updateField('timeDeclaredZulu', newVal);
  };

  // Keep each field's required highlight until that specific value is populated:
  // the Date stays red until a date is entered, the Zulu Time stays red until a
  // full HH:MM time is entered. The highlight is not cleared by the other field.
  const declaredDate = formData.timeDeclaredZulu ? formData.timeDeclaredZulu.split(' ')[0] : '';
  const declaredTimeDigits = formData.timeDeclaredZulu
    ? (formData.timeDeclaredZulu.split(' ')[1] || '').replace(/[^0-9]/g, '')
    : '';
  const dateMissing = !!dateRequired && !declaredDate;
  const timeMissing = !!dateRequired && declaredTimeDigits.length < 4;
  const dateNeedsVerification: boolean = Boolean(verifyDate && declaredDate && !dateMissing);
  const timeNeedsVerification: boolean = Boolean(
    verifyTime && declaredTimeDigits.length >= 4 && !timeMissing,
  );

  return (
    <div className="field-row">
      <label className="field-label">{label || 'Time Declared'}</label>
      <div className="field-group field-group-compact">
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Date</span>
          <input
            type="date"
            value={declaredDate}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            onChange={handleDateChange}
            className={`field-input${dateMissing ? ' field-required-missing' : ''}${dateNeedsVerification ? ' field-triage-verify' : ''}`}
            title={
              dateNeedsVerification ? 'Auto-populated from Triage; verify before saving' : undefined
            }
          />
        </div>
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Zulu Time</span>
          <input
            type="text"
            value={getErtsTimeDisplay(formData.timeDeclaredZulu)}
            readOnly={isReadOnly}
            onChange={handleTimeChange}
            placeholder="HH:MM"
            maxLength={5}
            className={`field-input${timeMissing ? ' field-required-missing' : ''}${timeNeedsVerification ? ' field-triage-verify' : ''}`}
            title={
              timeNeedsVerification ? 'Auto-populated from Triage; verify before saving' : undefined
            }
          />
        </div>
      </div>
      <label className="field-label">Time Entered</label>
      <div className="field-group field-group-compact">
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Date</span>
          <input
            type="text"
            value={extractDate(formData.creationTime)}
            readOnly
            className="field-input"
          />
        </div>
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Zulu Time</span>
          <input
            type="text"
            value={extractTime(formData.creationTime)}
            readOnly
            className="field-input"
          />
        </div>
      </div>
    </div>
  );
};

export default TimeDeclaredSection;
